import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Bell, Check, Trash2, X, Inbox } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { onForegroundMessage } from "../lib/fcm";
import { resolveNotificationRoute } from "../lib/notificationRoutes";
import { useAuth } from "../contexts/AuthContext";

/**
 * Notification bell in the top nav.
 *  - Polls /api/notifications every 30s for the badge + recent list
 *  - Listens to FCM foreground messages and bumps the unread count instantly
 *  - Dropdown shows the latest 8 with type-coloured pills
 *  - "See all" goes to /app/notifications
 */
export default function NotificationCenter() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popRef = useRef(null);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const [position, setPosition] = useState(null);

  // Render outside the sticky header so its blur/overflow cannot clip the panel.
  // Keep both the footer and header visible; only the notification list scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = bellRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewport = window.visualViewport;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;
      const offsetLeft = viewport?.offsetLeft || 0;
      const panelWidth = Math.min(344, width - 24);
      const top = Math.max(offsetTop + 12, rect.bottom + 8);
      const availableHeight = Math.max(0, offsetTop + height - top - (width < 768 ? 90 : 16));
      setPosition({ width: panelWidth, left: Math.max(offsetLeft + 12, Math.min(rect.right - panelWidth, offsetLeft + width - panelWidth - 12)), top, maxHeight: Math.min(width < 768 ? 390 : 430, availableHeight) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=4");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (_) { /* silent — never break the shell */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Bump the unread badge instantly when an FCM foreground message arrives
  useEffect(() => {
    let off = () => {};
    try {
      off = onForegroundMessage((payload) => {
        const n = payload?.notification || {};
        const data = payload?.data || {};
        setUnread((u) => u + 1);
        setItems((prev) => [{
          id: data.notification_id || `live-${Date.now()}`,
          title: n.title || "iCaard",
          message: n.body || "",
          type: data.type || "system",
          is_read: false,
          created_at: new Date().toISOString(),
          _live: true,
        }, ...prev].slice(0, 4));
      });
    } catch (_) { /* ignore */ }
    return () => { try { off(); } catch (_) { /* ignore */ } };
  }, []);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (open && !popRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false); };
    const escape = (e) => { if (open && e.key === "Escape") { setOpen(false); bellRef.current?.focus(); } };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", escape); };
  }, [open]);

  const markOne = async (n) => {
    try {
      if (!n.is_read && !n._live) await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      if (!n.is_read) setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* ignore */ }
  };

  // Click handler: mark-as-read THEN deep-link to the relevant screen.
  const handleClick = (n) => {
    markOne(n);
    const route = resolveNotificationRoute(n, user?.role || "");
    if (route) {
      setOpen(false);
      nav(route);
    }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); setItems((p) => p.map((x) => ({ ...x, is_read: true }))); }
    catch (_) { /* ignore */ }
  };
  const remove = async (n, e) => {
    e?.stopPropagation();
    try { await api.delete(`/notifications/${n.id}`); setItems((p) => p.filter((x) => x.id !== n.id)); if (!n.is_read) setUnread((u) => Math.max(0, u - 1)); }
    catch (_) { /* ignore */ }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        data-testid="notification-bell"
        ref={bellRef}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        aria-controls={open ? "icaard-notifications-popup" : undefined}
        className="relative w-9 h-9 rounded-xl border border-[#26263A] hover:bg-[#13131E] inline-flex items-center justify-center text-white"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && position && createPortal(
        <div data-testid="notification-dropdown"
             ref={panelRef} id="icaard-notifications-popup" role="dialog" aria-label="Notifications"
             style={position}
             className="icaard-notifications">
          <div className="icaard-notifications-header flex items-center justify-between gap-2">
            <div>
              <div className="icaard-notifications-eyebrow">Notifications</div>
              <div className="icaard-notifications-summary">{unread > 0 ? `${unread} unread` : "You’re all caught up"}</div>
            </div>
            <div className="flex gap-1">
              {unread > 0 && (
                <button data-testid="notification-mark-all" onClick={markAll}
                  className="icaard-notifications-mark-all">
                  <Check size={13} /> Mark all read
                </button>
              )}
              <button onClick={() => { setOpen(false); bellRef.current?.focus(); }} aria-label="Close notifications" className="icaard-notifications-close"><X size={17} /></button>
            </div>
          </div>
          <ul className="icaard-notifications-list" data-testid="notification-list">
            {items.length === 0 && (
              <li className="icaard-notifications-empty"><Inbox size={25} /> No notifications yet.</li>
            )}
            {items.map((n) => (
              <li key={n.id} data-testid={`notification-item-${n.id}`}
                  className={`icaard-notification-item ${!n.is_read ? "is-unread" : ""}`}>
                <div className="flex items-start gap-2">
                  <button type="button" onClick={() => handleClick(n)} className="icaard-notification-content flex-1 min-w-0 text-left">
                    <div className="icaard-notification-meta">
                      <span className={`icaard-notification-type ${typeColor(n.type)}`}>{n.type || "system"}</span>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" />}
                      <span className="icaard-notification-date">{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    <div className="icaard-notification-title">{n.title}</div>
                    <div className="icaard-notification-message">{n.message}</div>
                  </button>
                  <button onClick={(e) => remove(n, e)} className="icaard-notification-delete" aria-label="Delete notification">
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <button data-testid="notification-see-all" onClick={() => { setOpen(false); nav("/app/notifications"); }}
            className="icaard-notifications-footer">
            View all notifications
          </button>
        </div>, document.body
      )}
    </div>
  );
}

const typeColor = (t) => {
  const map = {
    announcement: "text-[#FACC15]",
    card_activated: "text-[#10B981]",
    card_replaced: "text-[#10B981]",
    card_scanned: "text-[#A78BFA]",
    profile_viewed: "text-[#06B6D4]",
    transport: "text-[#10B981]",
    ticket: "text-[#7C3AED]",
    wallet: "text-[#10B981]",
    connection: "text-[#A78BFA]",
    message: "text-[#3B82F6]",
    subscription: "text-[#F59E0B]",
    system: "text-[#9CA3D4]",
  };
  return map[t] || "text-[#9CA3D4]";
};
